import { colorMatrix } from "../../../maps/components/MapComponent/constants";
import type { RatesDosageDataInterface } from "../../../pages/new-trail/types";

interface SeedColorProduct {
  id?: string;
  variety?: string;
}

export const getTrialSeedColor = (
  entry: RatesDosageDataInterface,
  protocolProducts: SeedColorProduct[] | undefined,
  trialGroups: Array<RatesDosageDataInterface[] | null | undefined> | undefined,
  groupRates: RatesDosageDataInterface[] | undefined
): string => {
  const getVarietyId = (row: RatesDosageDataInterface): string | undefined =>
    row.product_id ??
    protocolProducts?.find((product) => product.variety === row.variety)?.id;
  const varietyIds = new Set(
    (trialGroups ?? []).flatMap((group) =>
      (group ?? []).map(getVarietyId).filter(Boolean)
    )
  );

  return getCircleColor(
    getVarietyId(entry),
    entry.rate,
    Array.from(varietyIds, (variety) => ({ variety })),
    undefined,
    groupRates?.map((row) => ({ ...row, variety: getVarietyId(row) }))
  );
};

export const getCircleColor = (
  variety: string | undefined,
  rate: number | undefined,
  trialProducts: any,
  trialMeasureUnit?: any,
  ratesDosageList?: any
): string => {
  if (!trialProducts || !variety || !rate) {
    return "";
  }

  let idx = 0;
  if (trialProducts.length > 1) {
    idx = trialProducts.map((x: any) => x.variety).indexOf(variety) % 10;
  }

  const variantRates =
    ratesDosageList?.filter((item: any) => item.variety === variety) ?? [];

  if (variantRates.length === 0) {
    return "";
  }

  if (variantRates.length === 1) {
    const color = colorMatrix[idx]?.[50] ?? "";
    return color;
  }

  const rates = variantRates.map((item: any) => item.rate);

  const finalColor = getColorBasedOnRate(rate, rates, colorMatrix[idx], idx);

  return finalColor;
};

export function getCircleColorNutrient(
  data: RatesDosageDataInterface[],
  rate: number,
  key?: keyof RatesDosageDataInterface,
  plotIndex?: number
): string {
  if (!rate) {
    return "";
  }

  let valueKey: keyof RatesDosageDataInterface = "fertiliser_rate";
  if (key) {
    valueKey = key;
  }

  const rates = data.map((item) => (item[valueKey] as number) ?? 0);
  const blueColorShade = colorMatrix[plotIndex ?? 0] ?? {};

  const finalColor = getColorBasedOnRate(
    rate,
    rates,
    blueColorShade,
    plotIndex ?? 0
  );

  return finalColor;
}

const BIO_COLOR_CACHE: Record<number, string> = {};
let bioColorIndex = 0;
// CWTRC-2188: keep navy last; unselected treatments must not claim a color.
const BIO_HUES = [140, 265, 175, 20, 340, 50, 190, 300, 80, 210];

export function getCircleColorBiologicals(
  treatment: { treated?: boolean } | undefined,
  treatmentID: number | null
): string {
  if (!treatment?.treated) return "#DFE2E7";
  if (treatmentID === null || treatmentID === undefined || treatmentID === 0) {
    return "#DFE2E7";
  }

  if (Object.hasOwn(BIO_COLOR_CACHE, treatmentID)) {
    return BIO_COLOR_CACHE[treatmentID];
  }

  const hue = BIO_HUES[bioColorIndex % BIO_HUES.length];
  const color = hslToHex(hue, 90, 20);
  BIO_COLOR_CACHE[treatmentID] = color;
  bioColorIndex++;
  return color;
}

function hslToHex(hue: number, saturation: number, lightness: number): string {
  hue = Math.max(0, Math.min(360, hue));
  saturation = Math.max(0, Math.min(100, saturation)) / 100;
  lightness = Math.max(0, Math.min(100, lightness)) / 100;
  const c = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = lightness - c / 2;
  let red = 0;
  let green = 0;
  let blue = 0;

  if (hue < 60) {
    red = c;
    green = x;
  } else if (hue < 120) {
    red = x;
    green = c;
  } else if (hue < 180) {
    green = c;
    blue = x;
  } else if (hue < 240) {
    green = x;
    blue = c;
  } else if (hue < 300) {
    red = x;
    blue = c;
  } else {
    red = c;
    blue = x;
  }

  const rgb = [red, green, blue].map((v) => Math.round((v + m) * 255));
  return "#" + rgb.map((n) => n.toString(16).padStart(2, "0")).join("");
}

/**
 * Returns a dynamically calculated color for a rate.
 * Higher rates = darker colors = lower lightness (L).
 * Lower rates = lighter colors = higher lightness (L).
 */
function getColorBasedOnRate(
  rate: number,
  rates: number[],
  colorShade: any,
  idx: number,
  options?: { hue?: number; saturation?: number }
): string {
  if (!rates || rates.length === 0 || typeof rate !== "number") {
    // fallback to static color if needed
    return colorShade?.[50] ?? "";
  }

  // Find min/max for scaling, include `rate` to avoid edge-case mismatch
  const minRate = Math.min(...rates, rate);
  const maxRate = Math.max(...rates, rate);

  // Reverse: higher rate → darker (lower L)
  let lightness = 90;
  if (maxRate !== minRate) {
    lightness = 90 - ((rate - minRate) * (90 - 20)) / (maxRate - minRate); // 90 (light) to 20 (dark)
    lightness = Math.max(20, Math.min(90, lightness));
  }

  // Optionally vary hue per idx, or use options.hue, or fallback to blue (210)
  const hue =
    options?.hue ?? [210, 140, 265, 175, 20, 340, 50, 190, 300, 80][idx % 10];
  const saturation = options?.saturation ?? 60;

  return hslToHex(hue, saturation, lightness);
}
